/* eslint-disable no-nested-ternary */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Drawer, Slider, Themes, Button } from '@sainsburys-tech/bolt'
import { InView } from 'react-intersection-observer'
import { trackDataLayerEvent, eventNames } from '../../../helpers/tracking'
import { flyoutClickandCollect, flyoutCreditPlan, flyoutFastTrack } from './Flyout/Flyouts'
import { paymentPlanFlyout } from './Flyout/SecondFlyout'
import { Proposition } from './Proposition/Proposition'
import { PaymentPlan } from './Proposition/PaymentPlan'
import { Benefits } from './Benefits/Benefits'
import * as Styled from './M023.styles'

const { ArgosTheme } = Themes

// Used in Flyout title text and to assert against in e2e tests
export const argosCardTitle = 'Argos Card Credit Plans'
export const argosClickCollectTitle = 'Click & Collect'
export const argosFastTrackTitle = 'Fast Track Delivery'

/**
 * - M023 educates customers on propositions with a find out more CTA
 * that shows more information with the purpose of keeping customers within there buying/browsing journeys.
 * - M023's primary function is to show key propositions on events and trading pages.
 */
const M023 = ({ services }) => {
  const [visibleDrawer, setDrawerVisible] = useState(false)
  const [ctaDest, setCtaDestination] = useState('')
  const [isViewed, setViewed] = useState(false)
  const [termsAndConditions, setTermsAndConditions] = useState(null)

  if (!services.length) return null

  const slideProps = {
    itemsPerSlideXs: 1,
    itemsPerSlideSm: 1,
    itemsPerSlideMd: 1,
    itemsPerSlideLg: 1,
    isInfiniteCycle: true,
    className: 'xs-12--none'
  }

  const { type } = services[0]
  const isProposition = type === 'proposition'
  const isPaymentPlan = type === 'paymentPlan'
  const isAFSBanner = (destination = 'Credit') => isProposition && destination === 'Credit'

  const trackDrawerInteraction = (interaction) => {
    switch (interaction) {
      case 'open':
        trackDataLayerEvent(eventNames.afsBannerClicked)
        break
      case 'keep-on-shopping':
        trackDataLayerEvent(eventNames.afsDrawerCloseKeepOnShopping)
        break
      case 'close':
        trackDataLayerEvent(eventNames.afsDrawerCloseX)
        break
      default:
        break
    }
  }

  const drawerInteraction = (toggle = 'close', destination, terms) => {
    if (toggle === 'open') {
      setDrawerVisible(true)
      setCtaDestination(destination)
      setTermsAndConditions(terms)
    } else {
      setDrawerVisible(false)
    }

    if (isAFSBanner(destination)) {
      trackDrawerInteraction(toggle)
    }
  }

  const onComponentInView = () => {
    trackDataLayerEvent(eventNames.afsBannerInView)
    setViewed(true)
  }

  const propositions = () =>
    services.map((service, serviceIndex) => (
      <Proposition key={serviceIndex} service={service} serviceIndex={serviceIndex} onClick={drawerInteraction} />
    ))

  const paymentPlans = () =>
    services.map((service, serviceIndex) => (
      <PaymentPlan key={serviceIndex} service={service} serviceIndex={serviceIndex} onClick={drawerInteraction} />
    ))

  const chooseContent = () =>
    termsAndConditions
      ? {
          title: termsAndConditions.title,
          destination: paymentPlanFlyout(termsAndConditions)
        }
      : ctaDest === 'Credit'
      ? {
          title: argosCardTitle,
          destination: flyoutCreditPlan(() => {
            if (isAFSBanner(ctaDest)) {
              trackDataLayerEvent(eventNames.afsOffersHereClicked)
            }
          })
        }
      : ctaDest === 'Click & collect'
      ? {
          title: argosClickCollectTitle,
          destination: flyoutClickandCollect()
        }
      : {
          title: argosFastTrackTitle,
          destination: flyoutFastTrack()
        }

  const createDrawer = () => {
    const content = chooseContent()
    const footer = (
      <Button onClick={() => drawerInteraction('keep-on-shopping', ctaDest)} full data-test='m023-drawer-button'>
        Keep On Shopping
      </Button>
    )
    const drawerProps = {
      theme: ArgosTheme,
      heading: content.title,
      position: 'right',
      show: visibleDrawer,
      onClose: () => drawerInteraction('close', ctaDest),
      footer
    }
    return <Drawer {...drawerProps}>{content.destination}</Drawer>
  }

  return (
    <InView
      onChange={(inView) => inView && !isViewed && isAFSBanner() && onComponentInView()}
      style={{ width: '100%' }}
    >
      <Styled.Container>
        {isProposition &&
          (services.length > 1 ? <Slider {...slideProps} items={propositions(services)} /> : propositions(services))}
        {isPaymentPlan &&
          (services.length > 1 ? <Slider {...slideProps} items={paymentPlans(services)} /> : paymentPlans(services))}
        {type === 'benefit' && <Benefits services={services} />}
        {createDrawer()}
      </Styled.Container>
    </InView>
  )
}

M023.propTypes = {
  /** Array of benefits or propositions */
  services: PropTypes.arrayOf(
    PropTypes.shape({
      /** Type of the proposition configuration */
      type: PropTypes.oneOf(['benefit', 'proposition', 'paymentPlan']),
      /** Text for proposition heading */
      heading: PropTypes.string,
      /** APR rate */
      cardApr: PropTypes.string,
      /** Text the apr subheading message */
      aprMessage: PropTypes.string,
      /** Text for the subheading */
      subheading: PropTypes.string,
      /** Text for the body copy of the apr */
      aprCopy: PropTypes.string,
      /** Smaller copy below the apr copy */
      smallCopy: PropTypes.string,
      /** Even smaller text above the CTA */
      tsAndCs: PropTypes.string,
      /** Flyout text */
      termsAndConditions: PropTypes.shape({
        /** Flyout title text */
        title: PropTypes.string,
        /** Flyout rich text body copy */
        text: PropTypes.string
      }),
      /** CTA text */
      ctaStyle: PropTypes.string,
      /** Type of flyout that it opens */
      ctaDestination: PropTypes.oneOf(['Credit', 'Click & collect', 'Fasttrack - delivery']),
      /** Proposition image */
      mainImage: PropTypes.shape({
        /** Main image source */
        source: PropTypes.string.isRequired,
        /** Main image alt text */
        altText: PropTypes.string.isRequired
      })
    })
  )
}

export default M023
export { Proposition, Benefits }
